import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useDispatch, connect } from 'react-redux';

import {
  Button,
  Notification,
  ShellV2,
  Conditional,
  Dropdown,
  DropdownItem,
  Space,
  Text,
  Icon,
  Toggle,
  OpenHelpCenter,
} from '@cashfree-intl/coherent';
import { usePermissionCode } from '@cashfree-intl/auth';
import { setEnv } from 'containers/MainLayout/actions';

import {
  DEVELOPER_TOOLS_CHILDREN,
  DASHBOARD_EMBEDDABLE_COMPONENTS,
  PF_INTELLI_FORMS,
  PGDB_HIDE_REFUNDS_TAB,
  PGDB_SHOW_WORKFLOWS,
  PGDB_SHOW_INSIGHTS_AI,
  PGDB_SHOW_PRODUCT_LINKS,
  PGDB_SHOW_QUICK_CREATE,
  PGDB_SHOW_SLACK,
  PG_DASHBOARD_SUMMARY_PATH,
  PG_RISK_SHIELD,
  PROD,
  TEST,
} from 'utils/constants';
import { getEventPageName } from 'utils/getEventPageName';
import { keyKeyMapping, menuItems } from 'containers/BaseLayout/constants';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

// import { UserDetailsContext } from 'contexts/UserDetailsContext';
import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';
import { MerchantContext } from 'contexts/MerchantContext';
import { MerchantDetailsContext } from 'contexts/MerchantDetailsContext';
import NotificationBar from 'containers/Notification/NotificationBar';
import NotificationPopover from 'containers/Notification/NotificationPopover';
import { PRODUCT_CONFIG_MAPPING } from 'containers/LandingPage/constants';
import PropTypes from 'prop-types';
import { basename } from 'store';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { fetchGammaTokenAction } from 'containers/DashboardContainer/actions';
import WithReadPermissions from 'components/WithReadPermission';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';

import {
  getBrowserData,
  getFlowwiseDefaultMenuPath,
  getItemFromLocalStorage,
  setItemsInLocalStorage,
} from 'utils/common';
import { useClickOutSideGen } from 'utils/hooks';
import { Image } from 'components/Image';
// import ReflectTab from 'components/ReflectTab';
import { PERM_CODES } from 'constants/permCodes';
import { FLOWWISE_ANALYTICS_PATH } from 'containers/FlowwiseLayout/constants';
import {
  featuresItems,
  featuresItemsForImportMerchant,
  subProductsItems,
  subProductsItemsForImportMerchant,
} from 'baseConfig/mainLayout';
import TimeZoneDropdown from 'containers/TimeZoneDropdown';

import { updateFeatureItems, getRedirectionMapper } from './helpers';
import {
  utilitiesItems,
  DROPDOWN_OPTIONS,
  DEVELOPERS_TOOL_HEADER_MAPPING,
  DISPLAY_HEADER_MAPPING,
  EMBEDDABLE_COMPONENTS_KEY,
  FLOWWISE,
  OCC_ACTIVATED_SUB_PRODUCTS_KEY,
} from './constants';
import {
  StyledDropdownMenu,
  StyledKbd,
  StyledHeaderText,
  StyledDivider,
} from './styled';

import commandKey from '../../images/keyboard_command_key.svg';
import shiftKey from '../../images/keyboard_shift_key.svg';
import reducer from './reducer';
import saga from './saga';

function MainLayout({ children, fetchGammaToken }) {
  useInjectReducer({ key: 'mainLayout', reducer });
  useInjectSaga({ key: 'mainLayout', saga });

  const [searchParams] = useSearchParams();
  const qsEnv = searchParams.get('env') || null;
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const isTestEnv = qsEnv === TEST;
  const envVal = isTestEnv ? PROD : TEST;
  const [env, resetEnv] = useState(envVal || TEST);
  const pathNamePg = window.location.pathname;
  const urlArr = pathname.split('/') || [];
  const [selectedProduct, setSelectedProduct] = useState({
    key: 'pg',
    value: 'Payment Gateway',
    hasMultipleAccounts: false,
  });
  const { togglePanel, showPanel } = useContext(Notification.Context);
  const {
    isUPIEnabled,
    showRouterTab,
    routerStatus,
    featureToggles = {},
    isOnDemandEligible,
    easySplitStatus,
    easySplitEnabled,
    showInstantTab,
    showBatchLinkUpload,
    productStatus: merchantProductStatus,
    isVbaEnabled,
    isShopifyMerchant,
    isFlowwiseActive,
    isMerchantImportEnabled,
    selectedTimeZone,
    setSelectedTimeZone,
  } = useContext(MerchantContext);

  const dispatch = useDispatch();

  const { merchantDetails } = useContext(MerchantDetailsContext);
  const isPGEnabled = usePermissionCode(PERM_CODES.PG_VIEW);
  // const { userDetails } = useContext(UserDetailsContext); TODO: will un-comment once partners' issue of showing actual partners information is figured out.

  const accountDetails = {
    accountName: merchantDetails.merchantName,
    accountEmailId: merchantDetails.merchantEmail,
  };
  const showWorkflows =
    PGDB_SHOW_WORKFLOWS in featureToggles &&
    featureToggles[PGDB_SHOW_WORKFLOWS];
  const showProductLinks =
    PGDB_SHOW_PRODUCT_LINKS in featureToggles &&
    featureToggles[PGDB_SHOW_PRODUCT_LINKS];
  const showApps =
    PGDB_SHOW_SLACK in featureToggles && featureToggles[PGDB_SHOW_SLACK];
  const hideRefundsTab =
    PGDB_HIDE_REFUNDS_TAB in featureToggles &&
    featureToggles[PGDB_HIDE_REFUNDS_TAB];
  const showRiskShield =
    PG_RISK_SHIELD in featureToggles && featureToggles[PG_RISK_SHIELD];

  const showInsightsAI =
    PGDB_SHOW_INSIGHTS_AI in featureToggles &&
    featureToggles[PGDB_SHOW_INSIGHTS_AI];

  const showIntelliForms =
    PF_INTELLI_FORMS in featureToggles && featureToggles[PF_INTELLI_FORMS];

  const eposActivated = merchantProductStatus.EPOS === 'APPROVED';
  const showQuickCreate =
    PGDB_SHOW_QUICK_CREATE in featureToggles &&
    featureToggles[PGDB_SHOW_QUICK_CREATE];
  const isEmbeddableComponentsEnabled =
    DASHBOARD_EMBEDDABLE_COMPONENTS in featureToggles &&
    featureToggles[DASHBOARD_EMBEDDABLE_COMPONENTS];
  const showOCCActivated = merchantProductStatus.ECOM360 === 'APPROVED';

  const getNonApprovedOptions = items =>
    items.filter(item => {
      if (
        item.key === 'product-links' ||
        item.key === 'apps' ||
        item.key === 'qr-code' ||
        item.key === FLOWWISE ||
        item.key === 'risk-shield' ||
        item.key === 'vba-configuration' ||
        item.key === 'one-click-checkout' ||
        item.key === 'one-click-checkout-pitch' ||
        item.key === 'one-click-checkout-activated' ||
        OCC_ACTIVATED_SUB_PRODUCTS_KEY.includes(item.key) ||
        item.key === 'checkout-one' ||
        item.key === 'ecom360' ||
        item.key === EMBEDDABLE_COMPONENTS_KEY ||
        item.key === 'workflows'
      ) {
        return (
          (item.key === 'product-links' && showProductLinks) ||
          (item.key === 'apps' && showApps) ||
          (item.key === 'qr-code' && isUPIEnabled) ||
          (item.key === FLOWWISE && showRouterTab) ||
          (item.key === 'risk-shield' && showRiskShield) ||
          (item.key === 'vba-configuration' && isVbaEnabled) ||
          (item.key === 'ecom360' && isShopifyMerchant) ||
          (item.key === EMBEDDABLE_COMPONENTS_KEY &&
            isEmbeddableComponentsEnabled) ||
          (item.key === 'one-click-checkout' &&
            isShopifyMerchant &&
            merchantProductStatus.ECOM360 !== 'APPROVED') ||
          (item.key === 'one-click-checkout-pitch' &&
            !isShopifyMerchant &&
            merchantProductStatus.ECOM360 !== 'APPROVED') ||
          (item.key === 'one-click-checkout-activated' && showOCCActivated) ||
          (OCC_ACTIVATED_SUB_PRODUCTS_KEY.includes(item.key) &&
            merchantProductStatus.ECOM360 !== 'APPROVED') ||
          (item.key === 'workflows' && showWorkflows)
        );
      }
      return true;
    });

  const getChildren = items =>
    items.map(item => {
      if (item.children) {
        if (
          (item.key === 'softpos' &&
            merchantProductStatus.EPOS !== 'APPROVED') ||
          (item.key === FLOWWISE &&
            (routerStatus !== 'APPROVED' || showInsightsAI))
        ) {
          const { children: itemChildren, ...updatedItem } = item;
          return {
            ...updatedItem,
            isParent: false,
          };
        }
        const filteredChildren = item.children.filter(val => {
          if (
            val.key === 'settlements/vendor' ||
            val.key === 'settlements/instant' ||
            val.key === 'settlements/on-demand' ||
            val.key === 'transactions/refunds' ||
            val.key === 'easy-split/vendors' ||
            val.key === 'easy-split/transactions' ||
            val.key === 'easy-split/refunds' ||
            val.key === 'easy-split/adjustments' ||
            val.key === 'easy-split/settlements' ||
            val.key === 'payment-links/batch' ||
            val.key === 'flowwise/insights-ai' ||
            val.key === 'payment-forms/intelli-forms'
          ) {
            return (
              (val.key === 'settlements/vendor' && easySplitEnabled) ||
              (val.key === 'settlements/instant' && showInstantTab) ||
              (val.key === 'settlements/on-demand' && isOnDemandEligible) ||
              (val.key === 'transactions/refunds' && !hideRefundsTab) ||
              (val.key === 'easy-split/vendors' &&
                easySplitStatus === 'ACTIVE') ||
              (val.key === 'easy-split/transactions' &&
                easySplitStatus === 'ACTIVE') ||
              (val.key === 'easy-split/refunds' &&
                easySplitStatus === 'ACTIVE') ||
              (val.key === 'easy-split/adjustments' &&
                easySplitStatus === 'ACTIVE') ||
              (val.key === 'easy-split/settlements' &&
                easySplitStatus === 'ACTIVE') ||
              (val.key === 'payment-links/batch' && showBatchLinkUpload) ||
              (val.key === 'payment-forms/intelli-forms' && showIntelliForms) ||
              (val.key === 'flowwise/insights-ai' && showInsightsAI)
            );
          }
          return true;
        });
        if (filteredChildren.length < 1) {
          const { children: itemChildren, ...updatedItem } = item;
          return {
            ...updatedItem,
            isParent: false,
          };
        }
        return {
          ...item,
          children: filteredChildren,
          isParent: true,
        };
      }

      return item;
    });

  const getSidebarItems = () => {
    let allItems = {};

    let updatedFeatureItems;

    if (isMerchantImportEnabled) {
      updatedFeatureItems = updateFeatureItems(
        featuresItemsForImportMerchant,
        eposActivated,
      );
    } else {
      updatedFeatureItems = updateFeatureItems(featuresItems, eposActivated);
    }

    const featureItemsOption = getNonApprovedOptions(updatedFeatureItems);
    const finalListFeatureItems = getChildren(featureItemsOption);
    if (featureItemsOption.length > 0)
      allItems = {
        ...allItems,
        features: {
          items: finalListFeatureItems,
        },
      };

    let subProductsItemsOptions;

    if (isMerchantImportEnabled) {
      subProductsItemsOptions = getNonApprovedOptions(
        subProductsItemsForImportMerchant,
      );
    } else {
      subProductsItemsOptions = getNonApprovedOptions(subProductsItems);
    }

    const finalListSubProductsItems = getChildren(subProductsItemsOptions);
    if (finalListSubProductsItems.length > 0) {
      allItems = {
        ...allItems,
        subProducts: {
          label: 'PG PRODUCTS',
          items: finalListSubProductsItems,
        },
      };
    }
    const utilitesOptions = getNonApprovedOptions(utilitiesItems);
    const finalListUtilitiesItems = getChildren(utilitesOptions);
    if (finalListUtilitiesItems.length > 0) {
      allItems = {
        ...allItems,
        utilities: {
          items: finalListUtilitiesItems,
        },
      };
    }
    return allItems;
  };
  const data =
    (pathNamePg && pathNamePg.replace(`${basename}pg/`, '').split('/')) || [];

  let activeSidebarItem = '';
  if (data[0]) {
    activeSidebarItem += data[0];
  }
  if (data[1]) {
    activeSidebarItem += `/${data[1]}`;
  }
  if (activeSidebarItem === FLOWWISE && showRouterTab === true) {
    activeSidebarItem = 'flowwise/gateways';
  }
  if (
    activeSidebarItem === 'softpos/collection-point-management' &&
    merchantProductStatus.EPOS !== 'APPROVED'
  ) {
    activeSidebarItem = 'softpos';
  }
  if (
    activeSidebarItem === 'easy-split/vendors' &&
    easySplitStatus !== 'ACTIVE'
  ) {
    activeSidebarItem = 'easy-split';
  }
  if (activeSidebarItem.startsWith('payment-verification')) {
    activeSidebarItem = 'payment-verification';
  }
  if (
    isMerchantImportEnabled &&
    activeSidebarItem === 'settlements/settlement-details'
  ) {
    activeSidebarItem = 'settlements';
  }

  const [active, setActive] = useState(activeSidebarItem);
  const [sidebarItemsTest, setSidebarItemsTest] = useState({});
  const popover = useRef();

  let displayHeaderText = '';
  const iterateKeys = obj => {
    Object.keys(obj).forEach(keys => {
      if (keys.key === active) {
        displayHeaderText = keys.displayText;
      }

      const childKeys = urlArr.filter(item =>
        DEVELOPER_TOOLS_CHILDREN.includes(item),
      );

      if (childKeys.length === 1 && urlArr.includes('developers')) {
        displayHeaderText = DEVELOPERS_TOOL_HEADER_MAPPING[childKeys[0]];
        return;
      }

      if (typeof obj[keys] === 'object' && obj[keys] !== null) {
        obj[keys].items.forEach(item => {
          if (item.key === activeSidebarItem && !item.hideHeader) {
            displayHeaderText =
              DISPLAY_HEADER_MAPPING[item.key] || item.displayText;
            return;
          }
          if (item.isParent) {
            if (item.key === 'settlements') {
              item.children.forEach(child => {
                if (child.key === active) {
                  displayHeaderText = `${child.displayText} Settlements`;
                }
              });
            } else if (item.key === FLOWWISE) {
              item.children.forEach(child => {
                if (active === 'flowwise/configurations') {
                  if (data?.length > 2) {
                    displayHeaderText = 'Configuration Details';
                  } else {
                    displayHeaderText = 'Configurations';
                  }
                } else if (active === 'flowwise/insights-ai') {
                  displayHeaderText = '';
                } else if (child.key === active) {
                  displayHeaderText =
                    DISPLAY_HEADER_MAPPING[child.key] || child.displayText;
                }
              });
            } else {
              item.children.forEach(child => {
                if (
                  child.key === active &&
                  !(item.hideHeader || child.hideHeader)
                ) {
                  displayHeaderText =
                    DISPLAY_HEADER_MAPPING[child.key] || child.displayText;
                }
              });
            }
          }
        });
      }
    });
  };

  useClickOutSideGen(popover, () => {
    if (showPanel) {
      togglePanel();
    }
  });
  const updateURL = () => {
    navigate(`?env=${env}`);
  };
  const switchEnvAndUpdateUrl = () => {
    const updatedEnv = env === TEST ? PROD : TEST;
    setItemsInLocalStorage({ env });
    updateURL();
    resetEnv(updatedEnv);
  };
  const fetchGammaTokenAndSwitchEnv = () => {
    fetchGammaToken({
      cb: () => {
        if (env === TEST && getItemFromLocalStorage('gammaToken')) {
          switchEnvAndUpdateUrl();
        }
      },
    });
  };
  const onEnvSwitch = () => {
    if (env === TEST && !getItemFromLocalStorage('gammaToken')) {
      dispatch(setEnv(env));
      fetchGammaTokenAndSwitchEnv();
    } else {
      dispatch(setEnv(env));
      switchEnvAndUpdateUrl();
    }
  };
  const envObj = process.env;

  const redirectToProduct = val => {
    if (keyKeyMapping[val.key] === 'PG') {
      navigate(
        `${basename}${PG_DASHBOARD_SUMMARY_PATH}${
          searchParams.get('env') === TEST ? `?env=test` : '?env=prod'
        }`,
      );
      setActive('summary');
      iterateKeys(sidebarItemsTest);
    }
    if (keyKeyMapping[val.key] === 'CSP') {
      window.location.href = `${envObj.PAYOUTS_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }&productCode=${keyKeyMapping[val.key]}`;
    }
    if (keyKeyMapping[val.key] === 'CAC') {
      window.location.href = `${envObj.CAC_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }`;
    }
    if (keyKeyMapping[val.key] === 'CES') {
      const { pathname: pathNameCes, hash } = PRODUCT_CONFIG_MAPPING[
        keyKeyMapping[val.key]
      ];
      navigate(
        `${basename}${pathNameCes}${
          searchParams.get('env') === TEST ? '?env=test' : ''
        }${hash || ''}`,
        { [keyKeyMapping[val.key]]: 'APPROVED' },
      );
    }
    if (keyKeyMapping[val.key] === 'LRS') {
      window.location.href = `${envObj.LRS_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }`;
    }
    if (keyKeyMapping[val.key] === 'GCL') {
      window.location.href = `${envObj.GCL_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }`;
    }
    if (keyKeyMapping[val.key] === 'PF') {
      const { pathname: pathNamePf, hash } = PRODUCT_CONFIG_MAPPING[
        keyKeyMapping[val.key]
      ];
      navigate(
        `${basename}${pathNamePf}${
          searchParams.get('env') === TEST ? '?env=test' : ''
        }${hash || ''}`,
        { [keyKeyMapping[val.key]]: 'APPROVED' },
      );
    }
    if (keyKeyMapping[val.key] === 'ORDERS') {
      const { pathname: pathNameOrders, hash } = PRODUCT_CONFIG_MAPPING[
        keyKeyMapping[val.key]
      ];
      navigate(
        `${basename}${pathNameOrders}${
          searchParams.get('env') === TEST ? '?env=test' : ''
        }${hash || ''}`,
        { [keyKeyMapping[val.key]]: 'APPROVED' },
      );
    }
    if (keyKeyMapping[val.key] === 'SBC') {
      window.location.href = `${envObj.SBC_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }`;
    }
    if (keyKeyMapping[val.key] === 'VRS') {
      window.location.href = `${envObj.VRS_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }`;
    }
    if (keyKeyMapping[val.key] === 'CG') {
      window.location.href = `${envObj.PAYOUTS_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }&productCode=CG`;
    }
    if (keyKeyMapping[val.key] === 'EPOS') {
      navigate(
        `${basename}pg/softpos/collection-point-management?env=${
          searchParams.get('env') === TEST ? TEST : PROD
        }`,
      );
    }
    if (keyKeyMapping[val.key] === 'LRS') {
      window.location.href = `${envObj.LRS_BASE_PATH}?env=${
        searchParams.get('env') === TEST ? TEST : PROD
      }`;
    }
    if (keyKeyMapping[val.key] === 'FLOWWISE') {
      const subPath = isFlowwiseActive
        ? FLOWWISE_ANALYTICS_PATH
        : getFlowwiseDefaultMenuPath(routerStatus);
      navigate(
        `${basename}${envObj.FLOWWISE_BASE_PATH}${subPath}?env=${
          searchParams.get('env') === TEST ? TEST : PROD
        }`,
      );
    }
  };

  let hasSidebar = true;

  const splittedPathname1 = pathNamePg.split('/');
  if (
    splittedPathname1[3] === 'settings' ||
    pathNamePg.includes('risk-shield/pricing')
  ) {
    hasSidebar = false;
  }
  const handleProductSidebarClick = selectedItem => {
    if (selectedItem === FLOWWISE && showInsightsAI) {
      Analytics.trackEventWithNewNomenclature({
        eventName: EVENTS.INSIGHTS_AI.FLOWWISE_WEBAPP_V2_CLICKED,
        eventProperties: {
          source: 'product_card',
        },
      });
      const subPath = isFlowwiseActive
        ? FLOWWISE_ANALYTICS_PATH
        : getFlowwiseDefaultMenuPath(routerStatus);
      navigate(
        `${basename}${envObj.FLOWWISE_BASE_PATH}${subPath}${
          isTestEnv ? `?env=${TEST}` : ''
        }`,
      );
    } else if (selectedItem === 'softpos') {
      navigate(
        `${basename}pg/${selectedItem}/collection-point-management${
          searchParams.get('env') ? `?env=${searchParams.get('env')}` : ''
        }`,
      );
    } else {
      navigate(
        `${basename}pg/${selectedItem}${
          searchParams.get('env') ? `?env=${searchParams.get('env')}` : ''
        }`,
      );
    }
    setActive(selectedItem);
    iterateKeys(sidebarItemsTest);
  };
  const handleProductChange = () => {
    const pname = window.location.pathname;
    const splittedPathname = pname.split('/');
    const checkNesting = `${splittedPathname[3]}/${splittedPathname[4]}`;
    if (splittedPathname[3] === 'softpos') {
      setSelectedProduct({
        key: 'softpos',
        value: 'SoftPOS',
        hasMultipleAccounts: false,
        summary:
          'Accept payments via QR codes, payment links, or Tap & Pay from your mobile phone',
      });
    } else {
      setSelectedProduct({
        key: 'pg',
        value: 'Payment Gateway',
        hasMultipleAccounts: false,
      });
    }
    if (
      checkNesting === 'softpos/collection-point-management' &&
      merchantProductStatus.EPOS !== 'APPROVED'
    ) {
      setActive('softpos');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (
      checkNesting === 'softpos/collection-point-management' &&
      merchantProductStatus.EPOS === 'APPROVED'
    ) {
      setActive('softpos/collection-point-management');
      iterateKeys(sidebarItemsTest);
    }
  };

  useEffect(() => {
    handleProductChange();
    const sidebarList =
      (pathNamePg && pathNamePg.replace(`${basename}pg/`, '').split('/')) || [];
    activeSidebarItem = '';
    if (data[0]) {
      activeSidebarItem += sidebarList[0];
    }
    if (data[1]) {
      activeSidebarItem += `/${sidebarList[1]}`;
    }
    if (activeSidebarItem === 'settlements') {
      let activeItem;

      if (isMerchantImportEnabled) {
        activeItem = 'settlements';
      } else {
        activeItem = eposActivated
          ? 'settlements/online'
          : 'settlements/standard';
      }

      activeSidebarItem = activeItem;

      setActive(activeItem);
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (
      isMerchantImportEnabled &&
      activeSidebarItem === 'settlements/settlement-details'
    ) {
      const activeItem = 'settlements';
      activeSidebarItem = activeItem;
      setActive(activeItem);
      return;
    }
    if (activeSidebarItem === 'payment-links') {
      activeSidebarItem = 'payment-links/all';
      setActive('payment-links/all');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (activeSidebarItem === 'payment-forms') {
      activeSidebarItem = 'payment-forms/all';
      setActive('payment-forms/all');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (activeSidebarItem === 'customers') {
      activeSidebarItem = 'customers/active';
      setActive('customers/active');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (activeSidebarItem === 'standard') {
      activeSidebarItem = 'settlements/standard';
      setActive('settlements/standard');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (activeSidebarItem === 'disputes') {
      activeSidebarItem = 'transactions/disputes';
      setActive('transactions/disputes');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (activeSidebarItem === 'suspicious') {
      activeSidebarItem = 'transactions/suspicious';
      setActive('transactions/suspicious');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (
      activeSidebarItem === FLOWWISE &&
      showRouterTab !== true &&
      routerStatus !== 'APPROVED'
    ) {
      activeSidebarItem = FLOWWISE;
      setActive(activeSidebarItem);
      return;
    }
    if (
      activeSidebarItem === FLOWWISE &&
      showRouterTab === true &&
      routerStatus === 'APPROVED'
    ) {
      activeSidebarItem = 'flowwise/gateways';
      setActive(activeSidebarItem);
      return;
    }
    if (activeSidebarItem === 'summary') {
      activeSidebarItem = 'summary/overview';
      setActive(activeSidebarItem);
      return;
    }
    if (
      activeSidebarItem === 'softpos/collection-point-management' &&
      merchantProductStatus.EPOS !== 'APPROVED'
    ) {
      activeSidebarItem = 'softpos';
      setActive(activeSidebarItem);
      return;
    }
    if (
      activeSidebarItem === 'easy-split/vendors' &&
      easySplitStatus !== 'ACTIVE'
    ) {
      activeSidebarItem = 'easy-split';
      setActive(activeSidebarItem);
      return;
    }
    if (
      activeSidebarItem === 'easy-split/vendors' &&
      easySplitStatus === 'ACTIVE'
    ) {
      activeSidebarItem = 'easy-split/vendors';
      setActive(activeSidebarItem);
      return;
    }

    if (activeSidebarItem === 'offers/offers-description') {
      activeSidebarItem = 'offers';
      setActive(activeSidebarItem);
      return;
    }
    if (activeSidebarItem === 'vba-configuration') {
      activeSidebarItem = 'vba-configuration/accounts';
      setActive('vba-configuration/accounts');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (activeSidebarItem === 'one-click-checkout-activated/offers-analytics') {
      activeSidebarItem = 'one-click-checkout-activated/funnel-analytics';
      setActive(activeSidebarItem);
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (activeSidebarItem === 'one-click-checkout-pitch') {
      activeSidebarItem = 'one-click-checkout-pitch';
      setActive('one-click-checkout-pitch');
      iterateKeys(sidebarItemsTest);
      return;
    }
    if (
      typeof activeSidebarItem === 'string' &&
      activeSidebarItem.startsWith('customisation/')
    ) {
      activeSidebarItem = 'customisation';
      setActive('customisation');
      return;
    }
    if (activeSidebarItem.startsWith('payment-verification')) {
      activeSidebarItem = 'payment-verification';
      setActive('payment-verification');
      return;
    }
    setActive(activeSidebarItem);
  }, [window.location.pathname, env]);

  useEffect(() => {
    const result = getSidebarItems();
    setSidebarItemsTest(result);
  }, [featureToggles]);

  useEffect(() => {
    setTimeout(() => {
      const profile = document.querySelector('.profile-style');
      profile.addEventListener('click', () => {
        Analytics.track(EVENTS.SHELL_V2.NAV_PROFILE_CLICKED, {
          Response: 'click',
        });
      });
    }, 1000);
    if (hasSidebar) {
      setTimeout(() => {
        const collapseNav = document.querySelector(
          '[data-testid="chevron-circle"]',
        );
        collapseNav.addEventListener('click', () => {
          Analytics.track(EVENTS.SHELL_V2.NAV_SIDE_COLLAPSE, {
            Response: 'click',
          });
        });
      }, 1000);
    }
    Analytics.trackEventWithNewNomenclature({
      eventName: EVENTS.INSIGHTS_AI.ACTIVE_CONFIGURATION_STATUS,
      eventProperties: {
        isFlowwiseActive,
      },
    });
  }, []);

  const onLogoClick = () => {
    window.location.href = `${basename}landing${
      searchParams.get('env') ? `?env=${searchParams.get('env')}` : ''
    }`;
  };

  const onHelpCenterClick = () => {
    OpenHelpCenter.redirectToHelpCenter(window.location.pathname);
  };

  const handleMount = useCallback(() => {
    Analytics.trackEventWithNewNomenclature({
      eventName: `Page_Viewed_${getEventPageName(active || '')}`,
      eventProperties: {
        actionType: 'view',
      },
    });

    Analytics.trackEventWithNewNomenclature({
      eventName: `Page_Scrolled_${getEventPageName(active || '')}`,
      eventProperties: {
        scrollTrackingId: 'dashboard_page_scroll',
        scrollSelector: '.children-wrapper',
        actionType: 'scroll',
      },
    });
  }, [active]);

  iterateKeys(sidebarItemsTest);

  const browserData = getBrowserData();

  const isMacOS = browserData?.os?.name === 'macOS';

  const prodtheme = {
    sidebarBgColor: 'var(--BACKGROUND_LIGHT)',
  };
  const testtheme = {
    sidebarBgColor: 'var(--BACKGROUND_TEST)',
  };

  const quickCreate = () => (
    <Dropdown
      trigger={
        <Button
          secondary
          size="small"
          className="btn-text fill-btn"
          onClick={() => {
            Analytics.trackEventWithNewNomenclature({
              eventName: 'Quick_Create_Button',
            });
          }}
        >
          + Create
        </Button>
      }
      icon={null}
    >
      <StyledDropdownMenu>
        {DROPDOWN_OPTIONS.map(option => (
          <DropdownItem
            key={option.key}
            data-event-name="Quick_Create_Dropdown_Item"
            onClick={() => {
              Analytics.trackEventWithNewNomenclature({
                eventName: 'Quick_Create_Dropdown_Item',
                eventProperties: {
                  value: option.value,
                },
              });
              navigate(
                `${basename}pg/${getRedirectionMapper(
                  showOCCActivated,
                  option.value,
                )}${
                  searchParams.get('env') === TEST ? `&env=test` : '&env=prod'
                }`,
              );
            }}
          >
            <Space gap={1} direction="column">
              <Space alignItems="center" justifyContent="space-between">
                <Text>{option.text}</Text>
                <div>
                  <Conditional if={isMacOS}>
                    <StyledKbd>
                      <Image src={commandKey} alt="Command-Key" />
                    </StyledKbd>
                  </Conditional>
                  <Conditional if={!isMacOS}>
                    <StyledKbd>Ctrl</StyledKbd>
                  </Conditional>
                  +
                  <StyledKbd>
                    <Image src={shiftKey} alt="Shift-Key" />
                  </StyledKbd>
                  +<StyledKbd>{option.shortcutKey}</StyledKbd>
                </div>
              </Space>
              <Text
                variant="b12"
                color="bodyLight"
                style={{ whiteSpace: 'pre-wrap', maxWidth: '320px' }}
              >
                {option.description}
              </Text>
            </Space>
          </DropdownItem>
        ))}
      </StyledDropdownMenu>
    </Dropdown>
  );

  return (
    <ShellV2>
      <ShellV2.GlobalHeader>
        <ShellV2.GlobalHeader.LeftContainer>
          <ShellV2.GlobalHeader.LeftContainer.Logo
            iconName="cf-compact"
            handleLogoClick={onLogoClick}
          />
          <Conditional if={!isMerchantImportEnabled}>
            <ShellV2.GlobalHeader.LeftContainer.ProductDropdown
              activationDetails={merchantProductStatus}
              isTest={isTestEnv}
              handleProductNameClick={val => {
                const prodName = keyKeyMapping[val.key];
                redirectToProduct(val);
                Analytics.track(EVENTS.SHELL_V2.NAV_BENTO_CLICKED, {
                  Product_Selected: keyKeyMapping[val.key],
                  Type:
                    !isTestEnv && merchantProductStatus[prodName] === 'APPROVED'
                      ? 'access'
                      : 'discover',
                });
              }}
              selected={selectedProduct}
            />
          </Conditional>
          <Conditional if={isMerchantImportEnabled}>
            <Space gap={4} className="ml-2">
              <StyledDivider className="my-2" />
              <Space gap={1} alignItems="center">
                <Icon name="pg-v2" />
                <StyledHeaderText color="globalHeaderText">
                  Payment Gateway
                </StyledHeaderText>
              </Space>
            </Space>
          </Conditional>
        </ShellV2.GlobalHeader.LeftContainer>

        <ShellV2.GlobalHeader.RightContainer>
          <ShellV2.GlobalHeader.RightContainer.Buttons>
            <Conditional if={!isMerchantImportEnabled}>
              <Conditional if={showQuickCreate}>{quickCreate()}</Conditional>
              <Button
                secondary
                size="small"
                className="btn-text fill-btn"
                onClick={() => {
                  setTimeout(() => {
                    window.location.href = `${
                      process.env.COMMON_BASE_PATH
                    }/developers${
                      searchParams.get('env')
                        ? `?env=${searchParams.get('env')}`
                        : ''
                    }`;
                  }, 50);
                }}
              >
                Developers
              </Button>
            </Conditional>
            <Conditional if={isMerchantImportEnabled}>
              <Space gap={1}>
                <Toggle
                  secondary
                  size="small"
                  className="btn-text fill-btn"
                  onToggle={onEnvSwitch}
                  active={searchParams.get('env') === TEST}
                />
                <Text color="placeholder">Sandbox</Text>
              </Space>
            </Conditional>
            <Conditional if={!isMerchantImportEnabled}>
              <Button
                secondary
                size="small"
                className="btn-text fill-btn"
                onClick={onEnvSwitch}
              >
                Switch to {searchParams.get('env') === TEST ? 'Prod' : 'Test'}
              </Button>
            </Conditional>
          </ShellV2.GlobalHeader.RightContainer.Buttons>
          <Conditional if={isMerchantImportEnabled}>
            <TimeZoneDropdown
              isMerchantImportEnabled={isMerchantImportEnabled}
              setSelectedTimeZone={setSelectedTimeZone}
              selectedTimeZone={selectedTimeZone}
            />
          </Conditional>
          <NotificationPopover isShellV2 />
          <ShellV2.GlobalHeader.RightContainer.HelpCenter
            handleHelpCenterClick={onHelpCenterClick}
          />
          <ShellV2.GlobalHeader.RightContainer.Settings
            handleSettingsClick={async () => {
              await Analytics.track(EVENTS.SHELL_V2.NAV_SETTINGS_CLICKED, {
                Response: 'click',
              });
              window.location.href = `${process.env.COMMON_BASE_PATH}/settings`;
            }}
          />
          <ShellV2.GlobalHeader.RightContainer.Profile
            profileMenuOptions={menuItems}
            merchantAccountDetails={
              accountDetails.accountEmailId
                ? accountDetails
                : { accountName: '', accountEmailId: '' }
            }
            handleManageAccountClick={async () => {
              await Analytics.track(
                EVENTS.SHELL_V2.NAV_PROFILE_MANAGE_ACCOUNT_CLICKED,
                {
                  Response: 'click',
                },
              );
              window.location.href = `${
                process.env.COMMON_BASE_PATH
              }/profile/my-information`;
            }}
            hideOldNavigationSwitch
          />
        </ShellV2.GlobalHeader.RightContainer>
        {searchParams.get('env') === TEST && (
          <ShellV2.GlobalHeader.Banner.TextBanner
            bannerText={
              isMerchantImportEnabled ? 'Sandbox' : 'Test Environment'
            }
          />
        )}
      </ShellV2.GlobalHeader>
      <NotificationBar />
      <ShellV2.MainLayout>
        <Conditional if={isPGEnabled}>
          <Space direction="column">
            {hasSidebar &&
              (searchParams.get('env') === TEST ? (
                <ShellV2.MainLayout.Sidebar
                  activeItem={active}
                  sidebarItems={sidebarItemsTest}
                  onMenuItemClick={selectedItem =>
                    handleProductSidebarClick(selectedItem)
                  }
                  {...isMerchantImportEnabled && {
                    sidebarTheme: testtheme,
                    hideSidebarToggleIcon: true,
                  }}
                />
              ) : (
                <ShellV2.MainLayout.Sidebar
                  activeItem={active}
                  sidebarItems={sidebarItemsTest}
                  onMenuItemClick={selectedItem => {
                    handleProductSidebarClick(selectedItem);
                  }}
                  {...isMerchantImportEnabled && {
                    sidebarTheme: prodtheme,
                    hideSidebarToggleIcon: true,
                  }}
                />
              ))}
          </Space>

          <ShellV2.MainLayout.MainContent
            style={{
              padding: '0rem',
              overflow: 'hidden',
            }}
            onMount={handleMount}
            key={active}
          >
            {children(displayHeaderText)}
          </ShellV2.MainLayout.MainContent>
        </Conditional>
        <Conditional if={!isPGEnabled}>
          <WithReadPermissions
            description="to access Payment Gateway"
            merchantEmail={merchantDetails.merchantEmail}
          />
        </Conditional>
      </ShellV2.MainLayout>
    </ShellV2>
  );
}

MainLayout.propTypes = {
  children: PropTypes.any.isRequired,
  fetchGammaToken: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({});
const mapDispatchToProps = dispatch => ({
  fetchGammaToken: data => dispatch(fetchGammaTokenAction(data)),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(withConnect)(MainLayout);
